import { createContext, createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Webcam from 'react-webcam';
import { snackShow } from '../../../actions';
import RecordingStarted from 'sound/recording_started.mp3';
import { initImageList } from 'services/listerUploadVehicle/v2';
import { useForm } from 'react-final-form';
import { useParams } from 'react-router-dom';

type State = 'taking_photo' | 'confirming_photo';

type CameraViewContext = {
  imageLoading: boolean;
  setImageLoading: (loading: boolean) => void;
  isOpened: boolean;
  level1SectionId: string | null;
  isDamageSection: boolean;
  openCameraView: (level1SectionId: string, index: number, isDamageSection?: boolean, confirmedState?: boolean) => void;
  closeCameraView: () => void;
  state: State;
  confirmDialogOpened: boolean;
  cameraRef: React.RefObject<Webcam>;
  currentImageSrc: string | null;
  takeImage: () => void;
  setGalleryImage: (dataURL: string) => void;
  // changeSection: (s: string) => void;
  changeIndex: (keepState?: boolean) => void;
  setIndex: (newIndex: number, isGallery?: boolean) => void;
  imageIndex: number;
  closeConfirmDialog: () => void;
  openConfirmDialog: () => void;
  localRetakeImage: () => void;

  successErrorDialogOpened: 'success' | 'error' | null;
  openSuccessErrorDialog: (type: 'success' | 'error') => void;
  closeSuccessErrorDialog: () => void;
  localRemoveImage: () => void;
  selectPhoto: (i: number) => void;

  showHelperText: boolean;
  setShowHelperText: React.Dispatch<React.SetStateAction<boolean>>;

  isVideoMode: string | null;
  selectVideoMode: (field: string) => void;
  isRecording: boolean;
  recordedChunks: Blob[];
  startRecording: () => void;
  stopRecording: () => void;
  cancelRecording: () => void;
  type: 'default' | 'initial_image';
};

const defaultValue: CameraViewContext = {
  imageLoading: false,
  setImageLoading: () => {},
  selectPhoto: () => {},
  isOpened: false,
  level1SectionId: null,
  state: 'taking_photo',
  confirmDialogOpened: false,
  isDamageSection: false,
  openCameraView: () => {},
  closeCameraView: () => {},
  cameraRef: createRef(),
  currentImageSrc: null,
  takeImage: () => {},
  setGalleryImage: () => {},
  // changeSection: () => {},
  imageIndex: 0,
  changeIndex: () => {},
  setIndex: () => {},
  closeConfirmDialog: () => {},
  openConfirmDialog: () => {},
  localRetakeImage: () => {},
  successErrorDialogOpened: null,
  openSuccessErrorDialog: () => {},
  closeSuccessErrorDialog: () => {},
  localRemoveImage: () => {},

  showHelperText: false,
  setShowHelperText: () => {},

  isVideoMode: null,
  selectVideoMode: () => {},
  isRecording: false,
  recordedChunks: [],
  startRecording: () => {},
  stopRecording: () => {},
  cancelRecording: () => {},
  type: 'default'
};

const CameraViewContext = createContext(defaultValue);

type ProviderProps = {
  children: React.ReactNode;
  type?: 'default' | 'initial_image';
};

export const CameraViewContextProvider: React.FC<ProviderProps> = ({ children, type = 'default' }) => {
  const cameraRef = useRef<Webcam>();
  const mediaRecorderRef = useRef<MediaRecorder>();

  const [isDamageSection, setIsDamageSection] = useState(false);
  const [level1SectionId, setLevel1SectionId] = useState<string | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState<string | null>(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [confirmDialogOpened, setConfirmDialogOpened] = useState(false);
  const [successErrorDialogOpened, setSuccessErrorDialogOpened] = useState<'success' | 'error' | null>(null);
  const [imageTakingSectionId, setImageTakingSectionId] = useState(false);

  const [isVideoMode, setIsVideoMode] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);

  const selectVideoMode = (field: string) => {
    setIsVideoMode(field);
    setIndex(-1);
  };

  const resetVideoMode = () => {
    setIsVideoMode(null);
    setIsRecording(false);
    setRecordedChunks([]);
  };

  const [showHelperText, setShowHelperText] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const [state, setState] = useState<State>('taking_photo');

  const audioRef = useRef<HTMLAudioElement>(new Audio());

  const dispatch = useDispatch();

  const { id } = useParams<{ id: string }>();
  const vehicleId = Number(id);

  const form = useForm();

  const openCameraView = (
    level1SectionId: string,
    initialIndex: number,
    damageSection = false,
    confirmedState = false
  ) => {
    resetVideoMode();
    setLevel1SectionId(level1SectionId);
    setImageIndex(initialIndex);
    setState(confirmedState ? 'confirming_photo' : 'taking_photo');
    setIsOpened(true);
    setIsDamageSection(damageSection);

    const fieldState = form.getFieldState(`${level1SectionId}.images`);
    const isExterior = level1SectionId === 'exterior.subsections.exterior';
    const hasImages = fieldState?.value && fieldState.value?.length > 0;

    dispatch(initImageList({ isOpened: isExterior || hasImages, vehicleId }));
  };

  const closeCameraView = () => {
    resetVideoMode();
    setIsOpened(false);
  };

  const takeImage = () => {
    console.log({ imageLoading, cameraRef: cameraRef.current });
    if (imageLoading) {
      return;
    }
    if (!cameraRef.current) {
      dispatch(snackShow({ type: 'error', message: 'Error while taking photo' }));
      return;
    }

    // const dataURL = cameraRef.current.getScreenshot();

    const video = document.getElementById('camera') as HTMLVideoElement;
    console.log(video, 'video');
    if (!video) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d', { colorSpace: 'srgb' });
    if (!context) {
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataURL = canvas.toDataURL('image/jpeg', 1.0);

    setCurrentImageSrc(dataURL);
    setState('confirming_photo');
  };

  const setGalleryImage = (dataURL: string) => {
    setCurrentImageSrc(dataURL);
    setState('confirming_photo');
  };

  const changeIndex = (keepState = false) => {
    setCurrentImageSrc(null);
    resetVideoMode();
    if (!keepState) {
      setState('taking_photo');
    } else {
      setState('confirming_photo')
    }
    setImageIndex(curr => curr + 1);
  };

  const setIndex = (newIndex: number, isGallery = false) => {
    if (newIndex !== -1) {
      resetVideoMode();
    }

    if (!isGallery) {
      setCurrentImageSrc(null);
      setState('taking_photo');
    }

    setImageIndex(newIndex);
  };

  const addRecordedChunk = useCallback(
    (e: BlobEvent) => {
      if (e.data.size > 0) {
        setRecordedChunks(curr => [...curr, e.data]);
      }
    },
    [setRecordedChunks]
  );

  const startRecording = useCallback(async () => {
    if (!cameraRef.current || !cameraRef.current.stream) return;

    try {
      if (audioRef.current) {
        audioRef.current.src = RecordingStarted;
        await audioRef.current.play();
      }
    } catch (error) {
      console.error(error, 'error while playing sound');
    }

    setIsRecording(true);
    mediaRecorderRef.current = new MediaRecorder(cameraRef.current.stream, {
      mimeType: 'video/mp4'
    });
    mediaRecorderRef.current.addEventListener('dataavailable', addRecordedChunk);
    mediaRecorderRef.current.start();
  }, [cameraRef, mediaRecorderRef, addRecordedChunk]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    setState('confirming_photo');
  }, [mediaRecorderRef, setIsRecording]);

  const cancelRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    setRecordedChunks([]);
  }, [mediaRecorderRef, setIsRecording, setRecordedChunks]);

  const localRemoveImage = () => {
    setImageIndex(curr => (curr > 0 ? curr - 1 : 0));
  };

  const localRetakeImage = () => {
    setCurrentImageSrc(null);
    setState('taking_photo');
    setRecordedChunks([]);
    setIsRecording(false);
  };

  const openConfirmDialog = () => setConfirmDialogOpened(true);
  const closeConfirmDialog = () => setConfirmDialogOpened(false);

  const openSuccessErrorDialog = (type: 'success' | 'error') => setSuccessErrorDialogOpened(type);
  const closeSuccessErrorDialog = () => setSuccessErrorDialogOpened(null);

  const selectPhoto = (index: number) => {
    resetVideoMode();
    setState('confirming_photo');
    setCurrentImageSrc(null);
    setImageIndex(index);
  };

  return (
    <CameraViewContext.Provider
      value={{
        // selectedSectionId,
        level1SectionId,
        setImageLoading,
        imageLoading,
        isOpened,
        // availableLevel2Sections,
        openCameraView,
        closeCameraView,
        isDamageSection,
        state,
        cameraRef: cameraRef as React.RefObject<Webcam>,
        takeImage,
        currentImageSrc,
        // changeSection,
        changeIndex,
        setIndex,
        imageIndex,
        confirmDialogOpened,
        openConfirmDialog,
        closeConfirmDialog,
        localRetakeImage,
        successErrorDialogOpened,
        openSuccessErrorDialog,
        closeSuccessErrorDialog,
        localRemoveImage,
        setShowHelperText,
        showHelperText,
        setGalleryImage,
        selectPhoto,
        isVideoMode,
        selectVideoMode,
        isRecording,
        startRecording,
        stopRecording,
        recordedChunks,
        cancelRecording,
        type
      }}
    >
      {children}
    </CameraViewContext.Provider>
  );
};

export const useCameraViewContext = () => {
  const context = useContext(CameraViewContext);
  return context;
};
